import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  FaStethoscope, FaSyringe, FaMicroscope, FaTooth, 
  FaHeartbeat, FaCut, FaXRay, FaHospital, 
  FaPaw, FaWeight, FaCalendarCheck, FaHome, FaPhone 
} from 'react-icons/fa';
import IconWrapper from '../components/IconWrapper';
import { Link } from 'react-router-dom';

// Animasyon için varyantlar
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.05,
      duration: 0.5,
      ease: "easeOut"
    }
  })
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const Hizmetler: React.FC = () => {
  // Kategoriler
  const categories = [
    { id: 'all', name: 'Tümü' },
    { id: 'preventive', name: 'Koruyucu Hekimlik' },
    { id: 'diagnostic', name: 'Tanı Hizmetleri' },
    { id: 'treatment', name: 'Tedavi Hizmetleri' },
    { id: 'special', name: 'Özel Hizmetler' }
  ];
  
  const [activeCategory, setActiveCategory] = useState('all');
  
  // Tüm hizmetler
  const allServices = [
    {
      id: 1,
      name: 'Genel Muayene',
      description: 'Evcil dostunuzun genel sağlık durumunu kapsamlı şekilde değerlendiren muayene.',
      icon: FaStethoscope,
      category: 'preventive',
      details: [
        'Genel fiziksel muayene',
        'Kalp ve akciğer kontrolü',
        'Göz, kulak ve ağız kontrolü',
        'Vücut ağırlığı takibi',
        'Genel sağlık değerlendirmesi'
      ]
    },
    {
      id: 2,
      name: 'Aşılama',
      description: 'Evcil hayvanınızı bulaşıcı hastalıklardan korumak için düzenli aşılama hizmeti.',
      icon: FaSyringe,
      category: 'preventive',
      details: [
        'Kuduz aşısı',
        'Karma aşılar',
        'Kennel cough (köpek gençlik hastalığı) aşısı',
        'Lösemi aşısı (kediler için)',
        'Aşı kartı ve takibi'
      ]
    },
    {
      id: 3,
      name: 'Laboratuvar Testleri',
      description: 'Hastalıkların teşhisi ve sağlık durumunun değerlendirilmesi için kapsamlı testler.',
      icon: FaMicroscope,
      category: 'diagnostic',
      details: [
        'Kan testleri',
        'İdrar analizi',
        'Dışkı muayenesi',
        'Biyokimya testleri',
        'Hormon testleri'
      ]
    },
    {
      id: 4,
      name: 'Diş Bakımı',
      description: 'Evcil hayvanınızın ağız ve diş sağlığını korumak için profesyonel bakım.',
      icon: FaTooth,
      category: 'treatment',
      details: [
        'Diş temizliği ve polisaj',
        'Diş çekimi',
        'Diş taşı temizliği',
        'Diş eti hastalıkları tedavisi',
        'Ağız bakımı danışmanlığı'
      ]
    },
    {
      id: 5,
      name: 'Kardiyoloji',
      description: 'Kalp ve damar hastalıklarının teşhis ve tedavisi için uzman hizmet.',
      icon: FaHeartbeat,
      category: 'diagnostic',
      details: [
        'EKG (Elektrokardiyogram)',
        'Ekokardiyografi',
        'Kalp hastalıkları teşhisi',
        'Kalp muayenesi',
        'Kalp hastalıklarının tedavisi'
      ]
    },
    {
      id: 6,
      name: 'Cerrahi Operasyonlar',
      description: 'Modern ekipmanlarla gerçekleştirilen cerrahi müdahaleler.',
      icon: FaCut,
      category: 'treatment',
      details: [
        'Yumuşak doku cerrahisi',
        'Kısırlaştırma operasyonları',
        'Ortopedik ameliyatlar',
        'Tümör çıkarma işlemleri',
        'Acil cerrahi müdahaleler'
      ]
    },
    {
      id: 7,
      name: 'Görüntüleme',
      description: 'Hastalıkların teşhisi için ileri görüntüleme teknikleri.',
      icon: FaXRay,
      category: 'diagnostic',
      details: [
        'Röntgen',
        'Ultrason',
        'Ekokardiyografi',
        'Endoskopi',
        'Dijital radyografi'
      ]
    },
    {
      id: 8,
      name: 'Yatılı Tedavi',
      description: 'Evcil hayvanınızın sürekli gözetim altında tedavi görmesi için yatılı hizmet.',
      icon: FaHospital,
      category: 'treatment',
      details: [
        '24 saat gözlem',
        'Serum ve ilaç tedavisi',
        'Post-operatif bakım',
        'Yoğun bakım hizmetleri',
        'Uzun süreli tedavi imkanı'
      ]
    },
    {
      id: 9,
      name: 'Parazit Kontrolü',
      description: 'İç ve dış parazitlere karşı koruma ve tedavi hizmetleri.',
      icon: FaPaw,
      category: 'preventive',
      details: [
        'İç parazit tedavisi',
        'Dış parazit kontrolü',
        'Pire ve kene önleme',
        'Koruyucu ilaç uygulamaları',
        'Düzenli parazit takibi'
      ]
    },
    {
      id: 10,
      name: 'Beslenme Danışmanlığı',
      description: 'Evcil hayvanınızın sağlıklı beslenmesi için profesyonel danışmanlık.',
      icon: FaWeight,
      category: 'special',
      details: [
        'Kişiselleştirilmiş beslenme planları',
        'Kilo yönetimi',
        'Özel diyet programları',
        'Beslenme takibi',
        'Besin takviyesi önerileri'
      ]
    },
    {
      id: 11,
      name: 'Sağlık Kontrolleri',
      description: 'Düzenli sağlık kontrolleri ile hastalıkların erken teşhisi.',
      icon: FaCalendarCheck,
      category: 'preventive',
      details: [
        'Yıllık sağlık kontrolleri',
        'Yaşa özel kontroller',
        'Kronik hastalık takibi',
        'Periyodik sağlık taramaları',
        'Sağlık karnesi oluşturma'
      ]
    },
    {
      id: 12,
      name: 'Evde Bakım Hizmetleri',
      description: 'Hareket kısıtlılığı olan hastalar için ev ziyareti hizmeti.',
      icon: FaHome,
      category: 'special',
      details: [
        'Ev ziyaretleri',
        'Yaşlı hayvan bakımı',
        'Post-operatif ev bakımı',
        'Kronik hastalık takibi',
        'Ağrı yönetimi'
      ]
    }
  ];
  
  // Aktif kategoriye göre hizmetleri filtreleme
  const filteredServices = activeCategory === 'all' 
    ? allServices 
    : allServices.filter(service => service.category === activeCategory);
  
  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary to-primary-dark text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-display font-bold mb-4">
              Hizmetlerimiz
            </h1>
            <p className="text-xl text-blue-100">
              Evcil dostlarınıza sunduğumuz kapsamlı veteriner hizmetleri
            </p>
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-8 bg-white shadow-sm sticky top-16 z-20">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-2 md:gap-4">
            {categories.map((category, index) => (
              <button
                key={index}
                className={`px-4 py-2 rounded-full transition-all ${
                  activeCategory === category.id
                    ? 'bg-primary text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                onClick={() => setActiveCategory(category.id)}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
          >
            {filteredServices.map((service, index) => (
              <motion.div 
                key={service.id}
                className="bg-white rounded-2xl shadow-md overflow-hidden transform transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
                variants={fadeIn}
                custom={index}
              >
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    <div className={`w-14 h-14 flex items-center justify-center rounded-full ${
                      service.category === 'preventive' ? 'bg-primary-light/20 text-primary' :
                      service.category === 'diagnostic' ? 'bg-secondary-light/20 text-secondary' :
                      service.category === 'treatment' ? 'bg-accent-light/20 text-accent' :
                      'bg-green-100 text-green-600'
                    }`}>
                      <IconWrapper icon={service.icon} className="text-2xl" />
                    </div>
                    <h3 className="ml-4 text-lg font-semibold text-gray-800">{service.name}</h3>
                  </div>
                  <p className="text-gray-600 mb-6">{service.description}</p>
                  <div>
                    <h4 className="font-medium text-gray-800 mb-2">Hizmet İçeriği:</h4>
                    <ul className="space-y-1">
                      {service.details.map((detail, i) => (
                        <li key={i} className="text-gray-600 flex items-start">
                          <span className="text-primary mr-2 text-lg">•</span> {detail}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                <div className={`h-1 w-full ${
                  service.category === 'preventive' ? 'bg-primary' :
                  service.category === 'diagnostic' ? 'bg-secondary' :
                  service.category === 'treatment' ? 'bg-accent' :
                  'bg-green-500'
                }`}></div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-primary text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Profesyonel Veteriner Hizmetleri
          </h2>
          <p className="text-lg mb-8">
            Can dostlarınızın sağlığı için uzman kadromuzla hizmetinizdeyiz.
          </p>
          <div className="flex justify-center gap-4">
            <a
              href="tel:+905533077616"
              className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary hover:bg-primary-dark transition-colors duration-300"
            >
              <FaPhone className="mr-2" />
              Hemen Arayın
            </a>
            <Link 
              to="/iletisim" 
              className="bg-secondary text-white hover:bg-white hover:text-primary px-6 py-2 rounded-full font-medium transition-all duration-300"
            >
              İletişime Geçin
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Hizmetler; 